import {
  getBuyingFormatSequence,
  type BuyingFormatSequence,
  type PurchasingPhase,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { BidBasis, BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import { interpUri } from '@b-stock/fe-core/bstock-react'
import type { ListingDtoPricingStrategyEnum } from '@b-stock/listing-api-client'
import type { OfferingDto } from '@b-stock/offering-api-client'
import type { SoldAs } from '@b-stock/order-api-client'
import type { DealsEnum, ListingTypeEnum } from '@b-stock/search-api-client'

import {
  getActivePhase,
  getDisplayPhase,
} from '@/buyer-portal/helpers/auctions/buyingFormat'
import soldAsFromBid from '@/buyer-portal/helpers/auctions/soldAsFromBid'
import { parseCustomDate } from '@/helpers/dateUtils'
import { isListingBuyNowActive } from '@/helpers/listingBuyNowActive'

import type { SearchResult } from './listingSearchQuery'

const EMPTY_STRING_ARRAY: string[] = []

/**
 * The current bid status for the user, given a listing's 3MP/Ent auction data.
 * Shared standalone so callers with a raw `SearchResult` (e.g. analytics
 * tracking) can reuse this without constructing a full facade instance.
 */
export const getBidStatus = (searchResult: SearchResult): BidStatus =>
  searchResult.auction3MP?.bidStatus ??
  searchResult.auctionEnt?.bidStatus ??
  BidStatus.NO_BID

/**
 * A facade class that provides a unified interface for accessing SearchResult data,
 * regardless of whether the data comes from 3MP or Enterprise systems.
 *
 * This class centralizes all the conditional logic for choosing between auction3MP
 * and auctionEnt data sources, providing a clean API for components that need to
 * display auction listing information.
 *
 * Add no computed values that change over time, such as the purchasing phase
 * or buying formats: callers compute them when they need them, with
 * `getDisplayBuyingFormats` or `useDisplayBuyingFormats`.
 *
 * @example
 * ```typescript
 * const facade = new CombinedListingSearchResult(searchResult)
 * console.log(facade.title) // Gets title from the appropriate source
 * console.log(facade.currentPrice) // Gets current price with proper fallbacks
 * console.log(facade.endTime) // Gets computed end time as Date object
 * ```
 */
export class CombinedListingSearchResult {
  // Memoization cache for computed properties
  private _buyNowEndTime?: Date | null
  private _endTime?: Date
  private _unitPrice?: number
  private _pricePercentOfMsrp?: number
  private _winningBidAmount?: number

  /**
   * Creates a new facade instance for the given SearchResult
   * @param _searchResult - The SearchResult to wrap with unified access methods
   */
  constructor(private readonly _searchResult: SearchResult) {}

  // Basic Properties
  // These properties are available directly from the base SearchResult

  /**
   * The unique identifier for the search result
   * - For 3MP, this is listingId (mongo id)
   * - For ENT, this is auctionId and listingPrettyId (but NOT listingId)
   *   - e.g. 'hmd-i32696' (listingId is 'i32696')
   */
  get id(): string {
    return this._searchResult.id
  }

  /**
   * Bid basis
   * - BidBasis type is ABSOLUTE or RELATIVE.
   * - HOWEVER, this value is actually [2025-11-24] PRICE or RELATIVE
   * - Normalize to BidBasis: ABSOLUTE or RELATIVE
   */
  get bidBasis(): BidBasis {
    return this._searchResult.bidBasis === 'RELATIVE' ? 'RELATIVE' : 'ABSOLUTE'
  }

  get bidPrecision(): BidPrecision {
    return (
      this._searchResult.auction3MP?.bidPrecision ??
      this._searchResult.offering?.bidPrecision ??
      1
    )
  }

  /**
   * Whether this listing has Buy Now functionality
   */
  get buyNow(): boolean {
    return this._searchResult.buyNow
  }

  /**
   * The Buy Now price. No source carries an enterprise Buy Now price; an
   * enterprise Buy Now listing (types F/CF) cannot be bid on, so its start price
   * is the price it sells at.
   */
  get buyNowPrice(): number | null {
    const { auction3MP, auctionEnt, buyNow } = this._searchResult
    if (auction3MP) {
      return auction3MP.buyNow?.enabled
        ? (auction3MP.buyNow.price ?? null)
        : null
    }
    return buyNow ? (auctionEnt?.startPrice ?? null) : null
  }

  get nextMinBidAmount(): number | null {
    return this._searchResult.auction3MP?.nextMinBidAmount ?? null
  }

  /**
   * The title of the listing
   */
  get title(): string {
    return this._searchResult.title
  }

  /**
   * The unique listing identifier
   */
  get listingId(): string {
    return this._searchResult.listingId
  }

  /**
   * The auction's MongoDB _id, required by usePlaceBidMutation.
   * Normally sourced from the top-level auctionId field in the search API
   * response, but that's a denormalized copy written by the search indexer,
   * which can lag behind a just-created auction. auction3MP._id is the same
   * value fetched live (see augmentListings in listingSearchQuery.ts), so it
   * covers listings the indexer hasn't caught up to yet.
   */
  get auctionId(): string | null {
    return (
      this._searchResult.auctionId ?? this._searchResult.auction3MP?._id ?? null
    )
  }

  get lotId(): string | null | undefined {
    return this._searchResult.lotId
  }

  get offeringId(): string | null | undefined {
    return this._searchResult.offeringId
  }

  get storefrontId(): string {
    return this._searchResult.storefrontId
  }

  /**
   * The URL to view the auction details.
   * Misnomer: this is the listing URL. It does apply to offerings.
   * Computes the appropriate URL based on auction type and available data.
   */
  get auctionUrl(): string {
    // For 3MP auctions, use the listing ID
    if (this._searchResult.auction3MP?.listingId !== undefined) {
      return interpUri`/buy/listings/details/${this._searchResult.auction3MP.listingId}`
    }

    // For Enterprise auctions, use the auctionUrl if available
    if (this._searchResult.auctionUrl.length > 0) {
      return this._searchResult.auctionUrl
    }

    // Fallback to using the main listing ID
    return interpUri`/buy/listings/details/${this._searchResult.id}`
  }

  /**
   * The primary image URL for the listing
   */
  get primaryImageUrl(): string {
    return this._searchResult.primaryImageUrl
  }

  get sellerId(): string | undefined {
    return this._searchResult.sellerId
  }

  /**
   * The logo URL for the marketplace/site
   */
  get siteLogoUrl(): string {
    return this._searchResult.siteLogoUrl
  }

  /**
   * The name of the storefront/marketplace
   */
  get storefrontName(): string {
    return this._searchResult.storefrontName
  }

  /**
   * The buyer pays the seller directly for the goods. Absent on records the
   * indexing pipeline has not populated, which means not Seller Collects.
   */
  get sellerCollects(): boolean {
    return this._searchResult.sellerCollects === true
  }

  /**
   * The site abbreviation (e.g., 'tmp', 'ent-i')
   */
  get siteAbb(): string {
    return this._searchResult.siteAbb
  }

  /**
   * Whether an AI-generated manifest summary exists for this lot.
   *
   * Named for what the search field means rather than what it is called: the
   * API's `manifest` boolean reports summary existence, not whether the lot has
   * a manifest.
   */
  get hasManifestSummary(): boolean {
    return !!this._searchResult.manifest
  }

  /**
   * The full region string (e.g., "Dallas, TX, United States")
   */
  get region(): string {
    return this._searchResult.region
  }

  /**
   * The seller's ZIP code
   */
  get sellerZipCode(): string {
    return this._searchResult.sellerZipCode
  }

  /**
   * The condition.
   */
  get condition(): string[] {
    return this._searchResult.condition
  }

  /**
   * The displayed condition.
   */
  get displayedCondition(): string {
    return this._searchResult.displayedCondition ?? ''
  }

  /**
   * The pricing strategy (e.g., 'AUCTION', 'MAKE_AN_OFFER')
   */
  get pricingStrategy(): ListingDtoPricingStrategyEnum {
    return this._searchResult.pricingStrategy
  }

  /**
   * The inventory type
   */
  get inventoryType(): string {
    return this._searchResult.inventoryType
  }

  /**
   * The site/marketplace name (e.g., 'TMP', 'Enterprise')
   */
  get siteName(): string {
    return this._searchResult.siteName
  }

  /**
   * The listing type (e.g., 'CONTRACT', 'AUCTION')
   */
  get listingType(): ListingTypeEnum {
    return this._searchResult.listingType
  }

  /**
   * The country code (e.g., 'US', 'CA')
   */
  get countryCode(): string {
    return this._searchResult.countryCode
  }

  /**
   * The province/state code (e.g., 'TX', 'CA', 'ON')
   */
  get provinceCode(): string {
    return this._searchResult.provinceCode
  }

  /**
   * The currency code (e.g., 'USD', 'CAD')
   */
  get currency(): string {
    return this._searchResult.currency
  }

  /**
   * The number of popcorn time extensions
   */
  get nPopcorn(): number | undefined {
    return this._searchResult.nPopcorn
  }

  /**
   * The custom rank for the listing (used for analytics/recommendations)
   */
  get customRank(): number | undefined {
    return this._searchResult.customRank
  }

  /**
   * The random set label for the listing (used for A/B testing analytics)
   */
  get randomSetLabel(): number | undefined {
    return this._searchResult.randomSetLabel
  }

  // Conditional Properties
  // These properties require choosing between auction3MP and auctionEnt data

  get is3MP(): boolean {
    return this._searchResult.auction3MP !== null
  }

  get startPrice(): number | null {
    const { auction3MP, auctionEnt } = this._searchResult
    return auction3MP?.startPrice ?? auctionEnt?.startPrice ?? null
  }

  /**
   * Cached for performance since SearchResult data is immutable.
   */
  get winningBidAmount(): number {
    if (this._winningBidAmount !== undefined) {
      return this._winningBidAmount
    }

    /**
     * [2025-11-24] Bad data: when numberOfBids === 0, we have a winningBid
     */
    if (this.numberOfBids === 0) {
      this._winningBidAmount = 0
      return this._winningBidAmount
    }

    const { auction3MP, auctionEnt } = this._searchResult
    const auctionWinningBidAmount =
      auction3MP?.winningBidAmount ?? auctionEnt?.winningBidAmount ?? 0
    const listingWinningBidAmount = this._searchResult.winningBidAmount
      ? Number(this._searchResult.winningBidAmount)
      : 0

    /**
     * [2025-11-24] Seeing bad data in enterprise: auctionEnt.winningBidAmount
     * is still auctionEnt.startPrice; however, listing.winningBidAmount is set.
     * To robustly handle squirrelly data, use max.
     */
    this._winningBidAmount = Math.max(
      auctionWinningBidAmount,
      listingWinningBidAmount
    )

    return this._winningBidAmount
  }

  /**
   * Only treats values as set if they are a positive number (`||`, not `??`).
   * A Buy Now phase is priced at the Buy Now price — before purchasing too, when
   * it opens with Buy Now, and after it, for a Buy Now Only listing. The phase
   * is the listing's public one: a buyer's First Dibs window isn't considered.
   */
  get currentPrice(): number {
    const { buyNowPrice, startPrice, winningBidAmount } = this
    if (
      getDisplayPhase({
        listing: this._searchResult,
        enableFirstDibs: false,
      }) === 'BINO'
    ) {
      return buyNowPrice || startPrice || 0
    }
    return winningBidAmount || startPrice || 0
  }

  /**
   * The current bid status for the user
   */
  get bidStatus(): BidStatus {
    return getBidStatus(this._searchResult)
  }

  /**
   * The user's current bid amount (3MP only)
   */
  get bidAmount(): number | undefined {
    return this._searchResult.auction3MP?.bid?.bidAmount
  }

  /**
   * Whether the auction is closed/ended
   */
  get closed(): boolean {
    return (
      this._searchResult.auction3MP?.closed ??
      this._searchResult.auctionEnt?.closed ??
      false
    )
  }

  /**
   * Whether the auction is canceled
   */
  get canceled(): boolean {
    return (
      this._searchResult.auction3MP?.canceled ??
      this._searchResult.auctionEnt?.canceled ??
      false
    )
  }

  // Computed/Derived Properties
  // These properties involve calculations or transformations

  /**
   * The computed buy now end time as a Date object, cached for performance
   * Both endTime and buyNowEndTime are needed for live auction status updates.
   */
  get buyNowEndTime(): Date | null {
    if (this._buyNowEndTime !== undefined) {
      return this._buyNowEndTime
    }

    if (this._searchResult.buyNowEndTime) {
      const parsedBuyNowEndTime = parseCustomDate(
        this._searchResult.buyNowEndTime
      )
      this._buyNowEndTime = parsedBuyNowEndTime
      return this._buyNowEndTime
    } else {
      this._buyNowEndTime = null // not undefined, so we use cached value
    }
    return this._buyNowEndTime
  }

  /**
   * The computed end time as a Date object.
   * Cached for performance since SearchResult data is immutable.
   */
  get endTime(): Date {
    if (this._endTime !== undefined) {
      return this._endTime
    }

    // For Enterprise auctions, use auctionEnt data
    if (this._searchResult.auctionEnt?.endTime) {
      this._endTime = parseCustomDate(this._searchResult.auctionEnt.endTime)
      return this._endTime
    }

    // For 3MP auctions, prefer actualEndTime
    if (this._searchResult.auction3MP?.actualEndTime) {
      this._endTime = parseCustomDate(
        this._searchResult.auction3MP.actualEndTime
      )
      return this._endTime
    }

    // Fall back to the base endTime
    this._endTime = parseCustomDate(this._searchResult.endTime)
    return this._endTime
  }

  /**
   * Whether this is a B-Stock Blue inventory item
   */
  get isBstockBlue(): boolean {
    return this._searchResult.inventoryType === 'B-Stock Blue'
  }

  /**
   * How the listing is sold, in order of its purchasing phases. Without a 3MP
   * auction schedule, CBIN-shaped data reads as BINO_TO_AUCTION.
   */
  get buyingFormatSequence(): BuyingFormatSequence | null {
    return getBuyingFormatSequence(
      this._searchResult,
      this._searchResult.pricingStrategy
    )
  }

  // TODO BP-3332: read auction.biddingEnabled once the auction API returns it.
  /** Whether the listing has a bidding phase. */
  get biddingEnabled(): boolean {
    return this.buyingFormatSequence !== 'BINO'
  }

  /**
   * The phase purchasing is in right now — null before it starts and after it
   * ends. The listing's public phase: a buyer's First Dibs window isn't
   * considered (see `useActivePhase`).
   */
  get activePurchasingPhase(): PurchasingPhase | null {
    return getActivePhase({
      listing: this._searchResult,
      enableFirstDibs: false,
    })
  }

  /**
   * How the listing sold, known only to the viewer who won it. Null when it
   * hasn't sold to them.
   */
  get soldAs(): SoldAs | null {
    const auction = this._searchResult.auction3MP
    return auction ? soldAsFromBid(auction.closed, auction.bid) : null
  }

  /**
   * The offering listing status for Make An Offer listings
   */
  get offeringListingStatus(): string | undefined {
    return this._searchResult.offering?.listingStatus
  }

  /**
   * The offering data for Make An Offer listings
   */
  get offering(): OfferingDto | undefined {
    return this._searchResult.offering ?? undefined
  }

  /**
   * The packaging condition of the listing
   */
  get packagingCondition(): string[] {
    return this._searchResult.packagingCondition ?? EMPTY_STRING_ARRAY
  }

  /**
   * The categories array for the listing
   */
  get categories(): string[] {
    return this._searchResult.categories
  }

  get bstockGrade(): string[] | undefined {
    return this._searchResult.bstockGrade
  }

  get capacity(): string[] | undefined {
    return this._searchResult.capacity
  }

  get carrier(): string[] | undefined {
    return this._searchResult.carrier
  }

  get deal(): DealsEnum | null | undefined {
    return this._searchResult.deal
  }

  get memory(): string[] | undefined {
    return this._searchResult.memory
  }

  get model(): string[] | undefined {
    return this._searchResult.model
  }

  get problemFreeOrderRate(): number | null | undefined {
    return this._searchResult.problemFreeOrderRate
  }

  /**
   * The retail price (MSRP) for the listing
   */
  get retailPrice(): number | undefined {
    return this._searchResult.retailPrice || undefined
  }

  get sellerGrade(): string[] | undefined {
    return this._searchResult.sellerGrade
  }

  get typicalSellingPrice(): number | null | undefined {
    return this._searchResult.typicalSellingPrice
  }

  /**
   * The number of units/quantity for the listing
   */
  get units(): number | undefined {
    return this._searchResult.units ?? undefined
  }

  /**
   * The shipment type
   */
  get shipmentType(): string {
    return this._searchResult.shipmentType
  }

  /**
   * Shipment types supported by the quotes API (e.g. BINDING, FREE, BUYER_PICKUP)
   */
  get allowedShipmentTypes(): string[] {
    return this._searchResult.allowedShipmentTypes ?? []
  }

  /**
   * The seller's city location
   */
  get sellerCity(): string | undefined {
    return this._searchResult.sellerCity
  }

  get sellerLotId(): string[] | undefined {
    const { sellerLotId } = this._searchResult
    if (!Array.isArray(sellerLotId)) {
      return undefined
    }

    return sellerLotId.filter((id): id is string => typeof id === 'string')
  }

  /**
   * The number of bids for the auction.
   */
  get numberOfBids(): number {
    return (
      this._searchResult.auction3MP?.numberOfBids ||
      this._searchResult.auctionEnt?.numberOfBids ||
      this._searchResult.numberOfBids
    )
  }

  /**
   * Price basis for List View unit price, % of MSRP, and the Deal Graph's
   * deal ratio: when Buy Now is active and `buyNowPrice` is present, matches
   * the effective price used for the current-bid column; otherwise
   * `currentPrice`. If Buy Now is active but the price is absent, falls back
   * to `currentPrice` (partial API data).
   *
   * Public so consumers derive from the same basis the current-bid column
   * shows rather than re-deriving it and drifting out of agreement with it.
   */
  get priceForListDerivedMetrics(): number {
    if (
      isListingBuyNowActive({
        buyNow: this.buyNow,
        buyNowEndTime: this.buyNowEndTime,
        endTime: this.endTime,
      }) &&
      this.buyNowPrice != null &&
      this.buyNowPrice > 0
    ) {
      return this.buyNowPrice
    }
    return this.currentPrice
  }

  /**
   * Unit price calculated as effective list price divided by units.
   * Cached for performance since SearchResult data is immutable.
   */
  get unitPrice(): number {
    if (this._unitPrice !== undefined) {
      return this._unitPrice
    }

    // Calculate price per unit
    this._unitPrice =
      this.units !== undefined && this.units > 0
        ? this.priceForListDerivedMetrics / this.units
        : 0
    return this._unitPrice
  }

  /**
   * Price as percentage of MSRP with appropriate precision.
   * Cached for performance since SearchResult data is immutable.
   */
  get pricePercentOfMsrp(): number {
    if (this._pricePercentOfMsrp !== undefined) {
      return this._pricePercentOfMsrp
    }

    const retailPrice = this.retailPrice ?? 0
    const price = this.priceForListDerivedMetrics

    if (retailPrice <= 0 || price <= 0) {
      this._pricePercentOfMsrp = 0
      return this._pricePercentOfMsrp
    }

    this._pricePercentOfMsrp = price / retailPrice
    return this._pricePercentOfMsrp
  }

  /**
   * Any restrictions to the terms of sale defined by the seller for the listing
   */
  get termsRestrictions(): string[] | undefined {
    return this._searchResult.termsRestrictions
  }

  /**
   * Gets the original SearchResult for cases where direct access is needed.
   * This should be used sparingly - prefer using the facade's getter methods.
   */
  get originalSearchResult(): SearchResult {
    return this._searchResult
  }
}
